import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Card, CardActionArea, Typography, Chip, Avatar, Button,
  TextField, InputAdornment, IconButton, Skeleton, Pagination, Snackbar, Alert,
  alpha, useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import VideogameAssetIcon from '@mui/icons-material/VideogameAsset';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { useUser } from '../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import CharacterFormDialog from '../dialogs/CharacterFormDialog';
import {
  HubCampaignDetail, HubCharacterCard, HubCharacterDetail,
  hubListCharacters, hubGetCharacter,
} from '../../services/hubApi';

const PULSE_BLUE = '#4d7cff';
const PULSE_GRADIENT = `linear-gradient(135deg, #d9ff00, ${PULSE_BLUE})`;

const glassCard = {
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: '16px',
  transition: 'all 0.25s ease',
  '&:hover': {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    transform: 'translateY(-2px)',
  },
};

interface CharacterDatabaseViewProps {
  campaign: HubCampaignDetail;
  locked?: boolean;
}

const CharacterDatabaseView: React.FC<CharacterDatabaseViewProps> = ({ campaign, locked }) => {
  const theme = useTheme();
  const { isAuthenticated } = useUser();
  const navigate = useNavigate();

  const [items, setItems] = useState<HubCharacterCard[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<HubCharacterDetail | null>(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await hubListCharacters({ q: search || undefined, page });
      setItems(res.results);
      setCount(res.count);
    } catch {
      /* handle */
    }
    setLoading(false);
  }, [search, page]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = () => { setPage(1); setSearch(query.trim()); };
  const handleClear = () => { setQuery(''); setPage(1); setSearch(''); };

  const requireAuth = (): boolean => {
    if (!isAuthenticated) { navigate('/login'); return false; }
    return true;
  };

  const handleCreate = () => {
    if (locked || !requireAuth()) return;
    setEditing(null);
    setFormOpen(true);
  };

  const handleEdit = async (characterId: number) => {
    if (locked || !requireAuth()) return;
    try {
      const detail = await hubGetCharacter(characterId);
      if (detail.has_pending) {
        setSnack({ open: true, message: 'このキャラクターは現在審査中の編集があります', severity: 'error' });
        return;
      }
      setEditing(detail);
      setFormOpen(true);
    } catch {
      setSnack({ open: true, message: '読み込みに失敗しました', severity: 'error' });
    }
  };

  const totalPages = Math.ceil(count / 20);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{
            background: PULSE_GRADIENT, backgroundClip: 'text',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', mb: 0.5,
          }}
        >
          {campaign.title}
        </Typography>
        {campaign.description && (
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
            {campaign.description}
          </Typography>
        )}
      </Box>

      {/* Search + add */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="キャラクターを検索..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            endAdornment: query && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClear}><ClearIcon fontSize="small" /></IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={locked}
          startIcon={<AddIcon />}
          sx={{
            borderRadius: '100px', px: 2.5, whiteSpace: 'nowrap', textTransform: 'none',
            background: PULSE_GRADIENT, color: '#000', fontWeight: 600, boxShadow: 'none',
            '&.Mui-disabled': { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.3)' },
          }}
        >
          追加
        </Button>
      </Box>

      {/* Grid */}
      {loading ? (
        <Grid container spacing={2}>
          {[...Array(8)].map((_, i) => (
            <Grid item xs={6} sm={4} md={3} key={i}>
              <Skeleton variant="rounded" height={150} sx={{ borderRadius: '16px' }} />
            </Grid>
          ))}
        </Grid>
      ) : items.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <Box sx={{
            width: 64, height: 64, borderRadius: '16px', mx: 'auto', mb: 2.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
          }}>
            <InboxOutlinedIcon sx={{ fontSize: 28, color: 'rgba(255,255,255,0.2)' }} />
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 0.5 }}>
            {search ? '該当するキャラクターがありません' : 'まだキャラクターがありません'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
            「追加」から最初のキャラクターを登録できます
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {items.map(c => (
            <Grid item xs={6} sm={4} md={3} key={c.character_id}>
              <Card sx={{ ...glassCard, height: '100%' }}>
                <CardActionArea
                  onClick={() => handleEdit(c.character_id)}
                  sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}
                >
                  <Avatar
                    src={c.image_url || undefined}
                    sx={{ width: 64, height: 64, mb: 1.5, bgcolor: alpha(PULSE_BLUE, 0.15) }}
                  >
                    {!c.image_url && <PersonOutlineIcon sx={{ color: alpha(PULSE_BLUE, 0.6) }} />}
                  </Avatar>
                  <Typography
                    variant="body2" fontWeight={600} align="center"
                    sx={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {c.japanese_name || c.name}
                  </Typography>
                  {c.japanese_name && c.name !== c.japanese_name && (
                    <Typography
                      variant="caption" color="text.secondary" align="center"
                      sx={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {c.name}
                    </Typography>
                  )}
                  {c.game_count > 0 && (
                    <Chip
                      icon={<VideogameAssetIcon sx={{ fontSize: '12px !important' }} />}
                      label={c.game_count}
                      size="small"
                      sx={{
                        mt: 1, height: 20, fontSize: '0.65rem',
                        bgcolor: 'rgba(255,255,255,0.06)', color: 'text.secondary',
                      }}
                    />
                  )}
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            size="small"
            sx={{ '& .MuiPaginationItem-root': { borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)' } }}
          />
        </Box>
      )}

      <CharacterFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        campaignId={campaign.id}
        character={editing}
        onSubmitted={() => {
          setSnack({ open: true, message: '送信しました！承認後に公開されます', severity: 'success' });
          load();
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} variant="filled">{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CharacterDatabaseView;
